import React, { useState, useEffect } from 'react'
import {X, Plus, Trash2, Search} from 'lucide-react'

interface ComposicaoFormProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: any) => Promise<void>
  insumos: any[]
  composicoes?: any[]
  initialData?: any
  title: string
}

export const ComposicaoForm: React.FC<ComposicaoFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  insumos,
  composicoes = [],
  initialData,
  title
}) => {
  const [formData, setFormData] = useState({
    nome: '',
    unidadeMedida: 'un',
    observacoes: ''
  })

  const [itens, setItens] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [searchTerms, setSearchTerms] = useState<{[key: number]: string}>({})
  const [showDropdowns, setShowDropdowns] = useState<{[key: number]: boolean}>({})

  // Função para garantir que um valor seja número
  const ensureNumber = (value: any): number => {
    if (typeof value === 'number' && !isNaN(value)) {
      return value
    }
    const parsed = parseFloat(value)
    return isNaN(parsed) ? 0 : parsed
  }

  // Função para formatar preço de forma segura
  const formatPreco = (value: any): string => {
    const num = ensureNumber(value)
    return num.toFixed(2)
  }

  // Atualizar formData e itens quando initialData mudar
  useEffect(() => {
    if (initialData) {
      setFormData({
        nome: initialData.nome || '',
        unidadeMedida: initialData.unidadeMedida || 'un',
        observacoes: initialData.observacoes || ''
      })
      
      // Ao carregar dados iniciais, popular os termos de pesquisa com os nomes dos itens
      if (initialData.itens && Array.isArray(initialData.itens)) {
        const newSearchTerms: {[key: number]: string} = {}
        initialData.itens.forEach((item: any, index: number) => {
          if (item.itemId) {
            const displayName = getItemDisplayName(item.itemId, item.tipo)
            newSearchTerms[index] = displayName
          }
        })
        setSearchTerms(newSearchTerms)
      }
      
      setItens(initialData.itens || [])
    } else {
      setFormData({
        nome: '',
        unidadeMedida: 'un',
        observacoes: ''
      })
      setItens([])
    }
    // Limpar estados de pesquisa quando não há dados iniciais
    if (!initialData) {
      setSearchTerms({})
      setShowDropdowns({})
    }
  }, [initialData])

  // Função para obter nome de exibição do item (insumo ou composição)
  const getItemDisplayName = (itemId: string, tipo: string) => {
    if (tipo === 'insumo') {
      const insumo = insumos.find(i => i._id === itemId)
      return insumo ? `${insumo.nome} (${insumo.unidadeMedida})` : 'Item não encontrado'
    } else if (tipo === 'composicao') {
      const composicao = composicoes.find(c => c._id === itemId)
      return composicao ? `${composicao.nome} (${composicao.unidadeMedida}) - COMPOSIÇÃO` : 'Composição não encontrada'
    }
    return 'Item não identificado'
  }

  // Função para filtrar itens baseado no termo de pesquisa
  const getFilteredItems = (searchTerm: string) => {
    if (!searchTerm.trim()) {
      return { insumos, composicoes }
    }

    const term = searchTerm.toLowerCase()
    const filteredInsumos = insumos.filter(insumo => 
      insumo.nome.toLowerCase().includes(term) ||
      insumo.unidadeMedida.toLowerCase().includes(term)
    )
    const filteredComposicoes = composicoes.filter(composicao => 
      composicao.nome.toLowerCase().includes(term) ||
      composicao.unidadeMedida.toLowerCase().includes(term)
    )

    return { insumos: filteredInsumos, composicoes: filteredComposicoes }
  }

  const addItem = () => {
    const newItem = {
      itemId: '',
      tipo: 'insumo', // 'insumo' ou 'composicao'
      quantidade: 0,
      precoUnitario: 0,
      subtotal: 0
    }
    setItens(prevItens => [...prevItens, newItem])
  }

  const removeItem = (index: number) => {
    setItens(prevItens => prevItens.filter((_, i) => i !== index))
    // Limpar estados relacionados ao item removido
    const newSearchTerms = { ...searchTerms }
    const newShowDropdowns = { ...showDropdowns }
    delete newSearchTerms[index]
    delete newShowDropdowns[index]
    setSearchTerms(newSearchTerms)
    setShowDropdowns(newShowDropdowns)
  }

  const updateItem = (index: number, field: string, value: any) => {
    setItens(prevItens => {
      const newItens = [...prevItens]
      newItens[index] = { ...newItens[index], [field]: value }

      // Se mudou o item, atualizar o preço e tipo
      if (field === 'itemId') {
        // Primeiro procurar nos insumos
        const insumo = insumos.find(i => i._id === value)
        if (insumo) {
          const precoUnitario = ensureNumber(insumo.precoUnitario)
          newItens[index].precoUnitario = precoUnitario
          newItens[index].tipo = 'insumo'
          newItens[index].subtotal = ensureNumber(newItens[index].quantidade) * precoUnitario
        } else {
          // Procurar nas composições
          const composicao = composicoes.find(c => c._id === value)
          if (composicao) {
            const precoUnitario = ensureNumber(composicao.valorTotal)
            newItens[index].precoUnitario = precoUnitario
            newItens[index].tipo = 'composicao'
            newItens[index].subtotal = ensureNumber(newItens[index].quantidade) * precoUnitario
          }
        }
      }

      // Recalcular subtotal quando quantidade mudar
      if (field === 'quantidade') {
        const quantidade = ensureNumber(value)
        const precoUnitario = ensureNumber(newItens[index].precoUnitario)
        newItens[index].quantidade = quantidade
        newItens[index].subtotal = quantidade * precoUnitario
      }

      return newItens
    })
  }

  const handleSearchChange = (index: number, value: string) => {
    setSearchTerms(prev => ({ ...prev, [index]: value }))
    setShowDropdowns(prev => ({ ...prev, [index]: true }))
  }

  const handleItemSelect = (index: number, itemId: string, itemType: string) => {
    // Atualizar o item selecionado
    updateItem(index, 'itemId', itemId)
    
    // Atualizar o termo de pesquisa com o nome completo do item selecionado
    const displayName = getItemDisplayName(itemId, itemType)
    setSearchTerms(prev => ({ 
      ...prev, 
      [index]: displayName
    }))
    
    // Fechar o dropdown
    setShowDropdowns(prev => ({ ...prev, [index]: false }))
  }

  const valorTotal = itens.reduce((total: number, item: any) => {
    const subtotal = ensureNumber(item.subtotal)
    return total + subtotal
  }, 0)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (itens.length === 0) {
      alert('Adicione pelo menos um item à composição')
      return
    }

    setLoading(true)
    
    try {
      await onSubmit({
        ...formData,
        itens,
        valorTotal
      })
      onClose()
      // Só limpar o form se não estiver editando
      if (!initialData) {
        setFormData({
          nome: '',
          unidadeMedida: 'un',
          observacoes: ''
        })
        setItens([])
        setSearchTerms({})
        setShowDropdowns({})
      }
    } catch (error) {
      // Error handled by hook
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleCancel = () => {
    onClose()
    // Limpar form ao cancelar
    if (!initialData) {
      setFormData({
        nome: '',
        unidadeMedida: 'un',
        observacoes: ''
      })
      setItens([])
      setSearchTerms({})
      setShowDropdowns({})
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      {/* Modal com 80% de largura */}
      <div className="bg-white w-[80%] max-h-[90vh] flex flex-col rounded-lg shadow-xl">
        {/* Header fixo */}
        <div className="sticky top-0 bg-white border-b p-6 z-10 shadow-sm rounded-t-lg">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
            <button
              type="button"
              onClick={handleCancel}
              className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Conteúdo scrollável */}
        <div className="flex-1 overflow-y-auto">
          <form onSubmit={handleSubmit} className="p-6 space-y-8">
            {/* Informações básicas */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold mb-6">Informações da Composição</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nome da Composição *
                  </label>
                  <input
                    type="text"
                    name="nome"
                    value={formData.nome}
                    onChange={handleChange}
                    required
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Nome da composição"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Unidade de Medida *
                  </label>
                  <select
                    name="unidadeMedida"
                    value={formData.unidadeMedida}
                    onChange={handleChange}
                    required
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="un">Unidade (un)</option>
                    <option value="kg">Quilograma (kg)</option>
                    <option value="m">Metro (m)</option>
                    <option value="m2">Metro Quadrado (m²)</option>
                    <option value="m3">Metro Cúbico (m³)</option>
                    <option value="l">Litro (l)</option>
                    <option value="h">Hora (h)</option>
                    <option value="dia">Dia</option>
                  </select>
                </div>
              </div>

              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Observações
                </label>
                <textarea
                  name="observacoes"
                  value={formData.observacoes}
                  onChange={handleChange}
                  rows={3}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Observações sobre a composição"
                />
              </div>
            </div>

            {/* Itens da composição */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold">Itens da Composição</h3>
                <button
                  type="button"
                  onClick={addItem}
                  className="bg-green-600 text-white px-6 py-3 rounded-md hover:bg-green-700 flex items-center gap-2 transition-colors"
                >
                  <Plus size={20} />
                  Adicionar Item
                </button>
              </div>

              {itens.length === 0 ? (
                <div className="text-center py-12 text-gray-500 border border-dashed border-gray-300 rounded-md">
                  <div className="text-lg mb-2">Nenhum item adicionado</div>
                  <p>Clique em "Adicionar Item" para começar.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Header da tabela */}
                  <div className="hidden md:grid grid-cols-6 gap-4 p-4 bg-gray-100 rounded-md text-sm font-medium text-gray-700">
                    <div className="col-span-2">Insumo/Composição</div>
                    <div>Quantidade</div>
                    <div>Preço Unit.</div>
                    <div>Subtotal</div>
                    <div>Ação</div>
                  </div>
                  
                  {/* Itens */}
                  {itens.map((item: any, index: number) => {
                    const precoUnitario = ensureNumber(item.precoUnitario)
                    const subtotal = ensureNumber(item.subtotal)
                    const searchTerm = searchTerms[index] || ''
                    const showDropdown = showDropdowns[index] || false
                    const filteredItems = getFilteredItems(searchTerm)
                    
                    return (
                      <div key={index} className="bg-white p-4 rounded-lg border shadow-sm">
                        {/* Layout responsivo */}
                        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                          {/* Campo de pesquisa */}
                          <div className="md:col-span-2 relative">
                            <label className="block text-sm font-medium text-gray-700 mb-1 md:hidden">
                              Insumo/Composição
                            </label>
                            <div className="relative">
                              <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => handleSearchChange(index, e.target.value)}
                                onFocus={() => setShowDropdowns(prev => ({ ...prev, [index]: true }))}
                                placeholder="Digite para pesquisar ou clique na seta para ver todos"
                                className="w-full p-3 pr-10 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              />
                              <Search 
                                size={20} 
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" 
                              />
                            </div>

                            {/* Dropdown com itens filtrados */}
                            {showDropdown && (
                              <div className="absolute top-full left-0 right-0 z-20 bg-white border border-gray-300 rounded-md shadow-lg max-h-64 overflow-y-auto">
                                {/* Grupo de Insumos */}
                                {filteredItems.insumos.length > 0 && (
                                  <>
                                    <div className="px-4 py-3 bg-gray-100 text-sm font-semibold text-gray-600 uppercase tracking-wide">
                                      📦 Insumos ({filteredItems.insumos.length})
                                    </div>
                                    {filteredItems.insumos.map(insumo => (
                                      <button
                                        key={insumo._id}
                                        type="button"
                                        onClick={() => handleItemSelect(index, insumo._id, 'insumo')}
                                        className="w-full text-left px-4 py-3 hover:bg-blue-50 border-b border-gray-100 last:border-b-0"
                                      >
                                        <div className="font-medium">{insumo.nome}</div>
                                        <div className="text-sm text-gray-500">
                                          {insumo.unidadeMedida} • R$ {formatPreco(insumo.precoUnitario)}
                                        </div>
                                      </button>
                                    ))}
                                  </>
                                )}
                                
                                {/* Grupo de Composições */}
                                {filteredItems.composicoes.length > 0 && (
                                  <>
                                    <div className="px-4 py-3 bg-gray-100 text-sm font-semibold text-gray-600 uppercase tracking-wide">
                                      🔧 Composições ({filteredItems.composicoes.length})
                                    </div>
                                    {filteredItems.composicoes.map(composicao => (
                                      <button
                                        key={composicao._id}
                                        type="button"
                                        onClick={() => handleItemSelect(index, composicao._id, 'composicao')}
                                        className="w-full text-left px-4 py-3 hover:bg-blue-50 border-b border-gray-100 last:border-b-0"
                                      >
                                        <div className="font-medium">{composicao.nome}</div>
                                        <div className="text-sm text-gray-500">
                                          {composicao.unidadeMedida} • R$ {formatPreco(composicao.valorTotal)} • COMPOSIÇÃO
                                        </div>
                                      </button>
                                    ))}
                                  </>
                                )}

                                {/* Mensagem quando não há resultados */}
                                {filteredItems.insumos.length === 0 && filteredItems.composicoes.length === 0 && searchTerm.trim() && (
                                  <div className="px-4 py-6 text-center text-gray-500">
                                    Nenhum item encontrado para "{searchTerm}"
                                  </div>
                                )}

                                {/* Botão para fechar dropdown */}
                                <button
                                  type="button"
                                  onClick={() => setShowDropdowns(prev => ({ ...prev, [index]: false }))}
                                  className="w-full px-4 py-3 text-center text-sm text-gray-500 hover:bg-gray-50 border-t"
                                >
                                  Fechar lista
                                </button>
                              </div>
                            )}

                            {/* Campo hidden para o valor real */}
                            <input
                              type="hidden"
                              value={item.itemId}
                              required
                            />
                          </div>

                          {/* Quantidade */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1 md:hidden">
                              Quantidade
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              value={item.quantidade || ''}
                              onChange={(e) => updateItem(index, 'quantidade', e.target.value)}
                              placeholder="Qtd"
                              min="0"
                              required
                              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>

                          {/* Preço Unitário */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1 md:hidden">
                              Preço Unit.
                            </label>
                            <input
                              type="text"
                              value={`R$ ${formatPreco(precoUnitario)}`}
                              readOnly
                              className="w-full p-3 border border-gray-300 rounded-md bg-gray-50"
                            />
                          </div>

                          {/* Subtotal */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1 md:hidden">
                              Subtotal
                            </label>
                            <input
                              type="text"
                              value={`R$ ${formatPreco(subtotal)}`}
                              readOnly
                              className="w-full p-3 border border-gray-300 rounded-md bg-gray-50 font-medium"
                            />
                          </div>

                          {/* Ação */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1 md:hidden">
                              Ação
                            </label>
                            <button
                              type="button"
                              onClick={() => removeItem(index)}
                              className="w-full p-3 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                              title="Remover item"
                            >
                              <Trash2 size={20} className="mx-auto" />
                            </button>
                          </div>
                        </div>
                      </div>
                    )
                  })}

                  {/* Total */}
                  <div className="bg-blue-50 border border-blue-300 p-6 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-medium text-blue-800">Valor Total da Composição:</span>
                      <span className="text-2xl font-bold text-blue-900">R$ {formatPreco(valorTotal)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </form>
        </div>

        {/* Footer fixo com botões de ação */}
        <div className="sticky bottom-0 bg-white border-t p-6 shadow-lg rounded-b-lg">
          <div className="flex gap-4">
            <button
              type="submit"
              disabled={loading || itens.length === 0}
              onClick={handleSubmit}
              className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-lg"
            >
              {loading ? 'Salvando...' : 'Salvar Composição'}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="flex-1 bg-gray-300 text-gray-700 py-3 px-6 rounded-md hover:bg-gray-400 transition-colors text-lg"
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ComposicaoForm
